import React, { CSSProperties, KeyboardEventHandler, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { BillSummary } from '@enview/interface/types/bills/BillSummary';
import { flexRender, Header } from '@tanstack/react-table';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import './Tables.scss';

export const DraggableTableHeader = ({
  header,
  readOnly = false,
}: {
  header: Header<BillSummary, unknown>;
  readOnly?: boolean;
}) => {
  const isSelectCol = header.column.id === 'select-col';

  const sortableConfig =
    isSelectCol || readOnly
      ? { id: header.column.id, disabled: true }
      : { id: header.column.id };

  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable(sortableConfig);

  const [isHovered, setIsHovered] = useState(false);

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    width: header.column.getSize(),
  };

  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted(); // 'asc' | 'desc' | false
  const isAsc = sorted === 'asc';
  const isDesc = sorted === 'desc';

  const classes = ['dt-header', isDragging ? 'dragging' : ''].filter(Boolean).join(' ');

  const titleWrapperClasses = [
    'dt-title-wrapper',
    canSort ? 'sortable' : 'nosort',
    isHovered ? 'is-hovered' : '',
    sorted ? 'sorted' : '',
    isAsc ? 'sorted-asc' : '',
    isDesc ? 'sorted-desc' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (!canSort) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      header.column.toggleSorting();
    }
  };

  return (
    <th className={classes} colSpan={header.colSpan} ref={setNodeRef} style={style}>
      {header.isPlaceholder ? null : (
        <div className="dt-header-inner">
          {!isSelectCol && !readOnly && (
            <button
              {...attributes}
              {...listeners}
              aria-label={`Reorder column ${header.column.id}`}
              className="dt-drag-handle"
              type="button"
            >
              <FontAwesomeIcon className="dt-drag-handle___icon" icon={faEllipsis} />
            </button>
          )}

          <div
            className={titleWrapperClasses}
            onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
            onKeyDown={handleKeyDown}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            role={canSort ? 'button' : undefined}
            tabIndex={canSort ? 0 : -1}
          >
            <span className="dt-title">
              {flexRender(header.column.columnDef.header, header.getContext())}
            </span>
            <span className="dt-sort-icon-wrapper">
              <FontAwesomeIcon className="dt-sort-icon" icon={faCaretDown} />
            </span>
          </div>
        </div>
      )}
    </th>
  );
};

export default DraggableTableHeader;
